
import React, { useState, useEffect, createContext, useContext, useRef, useMemo } from 'react';
import { Button, colors, IconChevronLeft16 } from '@dhis2/ui';
import { useAuth } from '../auth/AuthContext';
import { getAuthHeader } from '../../utils/auth';
import PerformanceList from './components/ObjectiveList';
import { usePermissions, canDelete } from '../../utils/rolePermissions';
import ObjectiveForm from './components/ObjectiveForm';
import { useDataEngine, useDataQuery } from '@dhis2/app-runtime';
import { fetchObjectiveWithDetails } from './hooks/ObjectiveService';
import { ensurePositionAccessibility } from '../../utils/positionAccessibility';
import { POSITION_ATTRIBUTES } from '../staff/api/constants';
import { useKraVisibility } from '../organisationUsers/hooks/useKraVisibility';

// Constants
const BASE_URL = process.env.REACT_APP_DHIS2_BASE_URL || '';
const trackedEntityTypeId = 'OaAbpIs1cV7';
const programId = 'yiT00XKrrHP';
const programStageId = 'VIBtpvkpHFR';
const REPORTING_TO = 'SjP47qi3JeB';

// ✅ Helper: Extract attributes into a map
const extractAttributes = (attributes = []) => {
  const map = {};
  attributes.forEach((attr) => {
    map[attr.attribute] = attr.value;
  });
  return map;
};

// Query definitions
const EVENTS_QUERY = {
  events: {
    resource: 'events',
    params: ({ orgUnitId }) => ({
      program: programId,
      orgUnit: orgUnitId,
      fields: 'event,orgUnit,trackedEntityInstance,enrollment,dataValues[dataElement,value]',
      pageSize: 50
    })
  }
};

const PROGRAM_ORG_UNITS_QUERY = {
  orgUnits: {
    resource: `programs/${programId}/organisationUnits`,
    params: {
      fields: 'id,name'
    }
  }
};

const OU_PROGRAMS_QUERY = {
  ouPrograms: {
    resource: 'organisationUnits',
    id: ({ ouId }) => ouId,
    params: {
      fields: 'programs[id]'
    }
  }
};

const objectiveErrorCache = new Map();


// Context
const PerformanceContext = createContext();

export default function PerformanceManagementWrapper() {
  const engine = useDataEngine();
  const { user } = useAuth();
  const { canView, canEdit, canDelete } = usePermissions();
  const [onSaveCallback, setOnSaveCallback] = useState(null);
  const [onErrorCallback, setOnErrorCallback] = useState(null);

  const [data, setData] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(true);

  const getLevelFromRole = (roleId) => {
    const roleLevels = {
      'nLSSLch316I': 'CEO',      // CEO
      'adeqQmxfCLF': 'HOD',      // Head of Department  
      'gtMJNK0QVL7': 'TopStaff', // Top Staff
      'vdVjNaqxblC': 'SeniorStaff' // Senior Staff
    };
    return roleLevels[roleId] || 'SeniorStaff';
  };


  const [uiMode, setUiMode] = useState('list');
  const [editingLevelId, setEditingLevelId] = useState(null);
  const [editingObjective, setEditingObjective] = useState(null);
  const [expandedLevels, setExpandedLevels] = useState({});
  const [expandedObjectives, setExpandedObjectives] = useState({});
  const [selectedHigherLevelObjective, setSelectedHigherLevelObjective] = useState(null);
  const [protectedObjectives, setProtectedObjectives] = useState(new Set());
  const [childCounts, setChildCounts] = useState({});


  const [currentUserPosition, setCurrentUserPosition] = useState(null);
  const [reportingToPosition, setReportingToPosition] = useState(null);
  const [directReportPositions, setDirectReportPositions] = useState([]);
  const [reportingStructureLoaded, setReportingStructureLoaded] = useState(false);
  const [staffList, setStaffList] = useState([]);

  const [selectedGoal, setSelectedGoal] = useState(null);
  const [managerAccessGranted, setManagerAccessGranted] = useState(false);
  const { isKraEnabled, loading: kraLoading } = useKraVisibility();

  const canUserEditLevel = (levelId) => canEdit(user?.role, levelId);
  const canUserDeleteLevel = (levelId) => canDelete(user?.role, levelId);

  const toggleLevelExpansion = (levelId) => {
    setExpandedLevels(prev => ({ ...prev, [levelId]: !prev[levelId] }));
  };

  const toggleObjectiveExpansion = (levelId, objectiveId) => {
    setExpandedObjectives(prev => ({
      ...prev,
      [levelId]: {
        ...prev[levelId],
        [objectiveId]: !prev[levelId]?.[objectiveId]
      }
    }));
  };

  const grantManagerOrgUnitAccess = async (userId, managerOrgUnit) => {
    if (!userId || !managerOrgUnit) {
      console.log('❌ Missing userId or managerOrgUnit');
      return false;
    }

    try {
      console.log(`🔄 Granting manager org unit access:`, { userId, managerOrgUnit });

      // 1. Fetch full user with ALL details needed
      const { user: fullUser } = await engine.query({
        user: {
          resource: `users/${userId}`,
          params: {
            fields: 'id,firstName,surname,organisationUnits[id],dataViewOrganisationUnits[id],userCredentials[username]'
          }
        }
      });

      if (!fullUser) {
        console.error('❌ User not found');
        return false;
      }

      // 2. Check current access
      const currentOrgUnits = fullUser.organisationUnits?.map(ou => ou.id) || [];
      const currentDataViewUnits = fullUser.dataViewOrganisationUnits?.map(ou => ou.id) || [];

      const hasOrgUnitAccess = currentOrgUnits.includes(managerOrgUnit);
      const hasDataViewAccess = currentDataViewUnits.includes(managerOrgUnit);

      console.log('🔍 Current access status:', {
        hasOrgUnitAccess,
        hasDataViewAccess,
        currentOrgUnits,
        currentDataViewUnits
      });

      // 3. If already has access in BOTH arrays, return
      if (hasOrgUnitAccess && hasDataViewAccess) {
        console.log(`✅ User already has full access to org unit ${managerOrgUnit}`);
        return true;
      }

      // 4. Prepare updates - add to BOTH arrays if missing in either
      const updatedOrgUnits = hasOrgUnitAccess
        ? fullUser.organisationUnits
        : [...fullUser.organisationUnits, { id: managerOrgUnit }];

      const updatedDataViewOrgUnits = hasDataViewAccess
        ? fullUser.dataViewOrganisationUnits
        : [...fullUser.dataViewOrganisationUnits, { id: managerOrgUnit }];

      console.log('📝 Update payload:', {
        organisationUnits: updatedOrgUnits.map(ou => ou.id),
        dataViewOrganisationUnits: updatedDataViewOrgUnits.map(ou => ou.id)
      });

      // 5. Update user
      const updateData = {
        organisationUnits: updatedOrgUnits.map(ou => ({ id: ou.id })),
        dataViewOrganisationUnits: updatedDataViewOrgUnits.map(ou => ({ id: ou.id }))
      };

      console.log('📤 Sending update data:', updateData);

      const result = await engine.mutate({
        resource: `users/${userId}`,
        type: 'update',
        data: updateData
      });

      console.log(`✅ Successfully granted access to org unit ${managerOrgUnit}`);
      console.log('Update result:', result);

      return true;

    } catch (error) {
      console.error('❌ Failed to grant org unit access:', error);

      // Provide more detailed error information
      if (error.details) {
        console.error('Error details:', error.details);
      }
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      }

      return false;
    }
  };

  // Add this helper function near extractAttributes
  const getAllHierarchyOrgUnits = async (engine, userOrgUnits) => {
    const allOrgUnits = new Set();
    const topLevelOrgUnits = new Set();

    for (const orgUnit of userOrgUnits) {
      // Add the org unit itself
      allOrgUnits.add(orgUnit.id);

      try {
        // Get its full details including parent
        const { orgUnit: details } = await engine.query({
          orgUnit: {
            resource: `organisationUnits/${orgUnit.id}`,
            params: { fields: 'id,name,level,path,parent[id]' }
          }
        });

        if (details.path) {
          // Add all org units in the path
          const pathParts = details.path.split('/').filter(id => id);
          pathParts.forEach(id => allOrgUnits.add(id));

          // Get the top-level org unit (B2nLFRVH7yw)
          if (pathParts.length > 0) {
            topLevelOrgUnits.add(pathParts[0]); // First in path is top-level
          }
        }

        // If has parent, add parent ID
        if (details.parent?.id) {
          allOrgUnits.add(details.parent.id);
        }

      } catch (error) {
        console.warn('Could not get details for org unit:', orgUnit.id, error);
      }
    }

    // CRITICAL: Get ALL children of top-level org units (includes siblings)
    for (const topLevelId of topLevelOrgUnits) {
      try {
        const { organisationUnits } = await engine.query({
          organisationUnits: {
            resource: 'organisationUnits',
            params: {
              filter: `parent.id:eq:${topLevelId}`, // Get direct children
              fields: 'id',
              paging: false
            }
          }
        });

        organisationUnits?.organisationUnits?.forEach(ou => {
          allOrgUnits.add(ou.id);
        });

        // Also get ALL descendants (not just direct children)
        const { allDescendants } = await engine.query({
          allDescendants: {
            resource: 'organisationUnits',
            params: {
              filter: `path:like:${topLevelId}`,
              fields: 'id',
              paging: false
            }
          }
        });

        allDescendants?.organisationUnits?.forEach(ou => {
          allOrgUnits.add(ou.id);
        });

      } catch (error) {
        console.warn('Could not get children for top-level:', topLevelId, error);
      }
    }

    console.log('🌳 Final hierarchy org units:', Array.from(allOrgUnits));
    return Array.from(allOrgUnits);
  };


  const getCurrentUserPosition = async (userId, orgUnits) => {
    console.log('🔍 [GET CURRENT USER POSITION] Starting with accessibility check for user:', userId);

    if (!userId || !orgUnits?.length) {
      console.warn('❌ [ABORT] No user ID or org units provided');
      return null;
    }

    const normalizedUserId = String(userId).trim();

    // STEP 1: First try to ensure position is accessible
    console.log('🔧 Step 1: Ensuring position accessibility...');
    const accessiblePosition = await ensurePositionAccessibility(engine, userId, orgUnits);

    if (accessiblePosition) {
      console.log('✅ Position accessibility check passed - using accessible position');
      const attrs = extractAttributes(accessiblePosition.attributes);
      console.log('🎯 ACCESSIBLE POSITION FOUND:', {
        tei: accessiblePosition.trackedEntityInstance,
        orgUnit: accessiblePosition.orgUnit,
        positionTitle: attrs[POSITION_ATTRIBUTES.POSITION_TITLE],
        positionId: attrs[POSITION_ATTRIBUTES.POSITION_ID],
        reportingTo: attrs[REPORTING_TO]
      });
      return accessiblePosition;
    }

    console.log('🔄 Position accessibility fix attempted, proceeding with hierarchy search...');

    // STEP 2: Get ALL org units in the hierarchy
    console.log('🌳 Step 2: Building hierarchy from user org units:', orgUnits.map(ou => ou.id));
    const hierarchyOrgUnits = await getAllHierarchyOrgUnits(engine, orgUnits);
    const orgUnitParam = hierarchyOrgUnits.join(';');

    console.log('🌳 Searching in', hierarchyOrgUnits.length, 'hierarchy org units');

    // STEP 3: Search in hierarchy
    try {
      const response = await engine.query({
        positions: {
          resource: 'trackedEntityInstances',
          params: {
            trackedEntityType: 'UGGo0Ebjl0E', // Use actual trackedEntityType constant if available
            ou: orgUnitParam,
            ouMode: 'ACCESSIBLE',
            filter: `V66IFsw5kq6:eq:${normalizedUserId}`,
            fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]',
            pageSize: 10,
          },
        },
      });

      const positions = response.positions?.trackedEntityInstances || [];
      console.log(`✅ Found ${positions.length} positions in hierarchy for user ${userId}`);

      if (positions.length > 0) {
        const position = positions[0];
        const attrs = extractAttributes(position.attributes);
        console.log('🎯 HIERARCHY POSITION FOUND:', {
          tei: position.trackedEntityInstance,
          orgUnit: position.orgUnit,
          positionTitle: attrs[POSITION_ATTRIBUTES.POSITION_TITLE],
          positionId: attrs[POSITION_ATTRIBUTES.POSITION_ID],
          reportingTo: attrs[REPORTING_TO]
        });

        // Try to make this position accessible for future
        console.log('🔧 Attempting to make found position accessible...');
        try {
          await ensurePositionAccessibility(engine, userId, orgUnits);
        } catch (accessError) {
          console.warn('Could not make position accessible, but returning it anyway:', accessError);
        }

        return position;
      } else {
        console.log('⚠️ No positions found in hierarchy for user:', userId);

        // DEBUG: Check what positions ARE in the hierarchy
        const debugResponse = await engine.query({
          debugPositions: {
            resource: 'trackedEntityInstances',
            params: {
              trackedEntityType: 'UGGo0Ebjl0E',
              ou: orgUnitParam,
              ouMode: 'ACCESSIBLE',
              fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]',
              pageSize: 50,
            },
          },
        });

        const debugPositions = debugResponse.debugPositions?.trackedEntityInstances || [];
        console.log('🔍 DEBUG: First 5 positions in hierarchy:', debugPositions.map(p => ({
          tei: p.trackedEntityInstance,
          orgUnit: p.orgUnit,
          incumbent: extractAttributes(p.attributes)[POSITION_ATTRIBUTES.CURRENT_INCUMBENT],
          title: extractAttributes(p.attributes)[POSITION_ATTRIBUTES.POSITION_TITLE]
        })));

        // STEP 4: Try GLOBAL search in ALL org units
        console.log('🌍 Step 4: Trying GLOBAL search in ALL org units...');
        try {
          const globalResponse = await engine.query({
            globalPositions: {
              resource: 'trackedEntityInstances',
              params: {
                trackedEntityType: 'UGGo0Ebjl0E',
                filter: `V66IFsw5kq6:eq:${normalizedUserId}`,
                fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]',
                ouMode: 'ALL',
                pageSize: 5,
              },
            },
          });

          const globalPositions = globalResponse.globalPositions?.trackedEntityInstances || [];
          console.log(`🌍 Found ${globalPositions.length} positions globally`);

          if (globalPositions.length > 0) {
            const position = globalPositions[0];
            const attrs = extractAttributes(position.attributes);
            console.log('🎯 GLOBAL POSITION FOUND:', {
              tei: position.trackedEntityInstance,
              orgUnit: position.orgUnit,
              positionTitle: attrs[POSITION_ATTRIBUTES.POSITION_TITLE],
              positionId: attrs[POSITION_ATTRIBUTES.POSITION_ID],
              reportingTo: attrs[REPORTING_TO]
            });

            // If position is in a different org unit, grant access
            if (!orgUnits.some(ou => ou.id === position.orgUnit)) {
              console.log(`🔑 Granting access to org unit ${position.orgUnit} for user ${userId}`);
              await grantManagerOrgUnitAccess(userId, position.orgUnit);
            }

            return position;
          }
        } catch (globalError) {
          console.error('Global search failed:', globalError);
        }

        // STEP 5: No position found - return null
        console.log('❌ No position found for user in any org unit');
        return null;
      }
    } catch (error) {
      console.error('💥 Hierarchy position search failed:', error);
      return null;
    }
  };

  const getReportingToPosition = async (positionTEI) => {
    try {
      if (!positionTEI) return null;

      // Get the reportingTo TEI from current position attributes
      const attributes = extractAttributes(positionTEI.attributes);
      const reportingToTEI = attributes[REPORTING_TO];

      if (!reportingToTEI) return null;

      // ⭐ CRITICAL FIX: Include orgUnit in the fields
      const { positions } = await engine.query({
        positions: {
          resource: 'trackedEntityInstances',
          id: reportingToTEI,
          params: {
            fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]', // Added orgUnit
          },
        },
      });

      console.log('✅ [REPORTING TO POSITION FOUND]', {
        tei: positions?.trackedEntityInstance,
        orgUnit: positions?.orgUnit,
        hasOrgUnit: !!positions?.orgUnit
      });

      return positions || null;

    } catch (error) {
      console.error('❌ Error fetching reporting to position:', error);
      return null;
    }
  };

  const getDirectReports = async (positionTEI) => {
    try {
      if (!positionTEI) return [];

      console.log('🔍 [DIRECT REPORTS QUERY] Looking for positions reporting to:', positionTEI.trackedEntityInstance);

      const { positions } = await engine.query({
        positions: {
          resource: 'trackedEntityInstances',
          params: {
            trackedEntityType: 'UGGo0Ebjl0E',
            ouMode: 'ACCESSIBLE',
            filter: `${REPORTING_TO}:eq:${positionTEI.trackedEntityInstance}`,
            fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]',
            pageSize: 100,
          },
        },
      });

      const directReports = positions.trackedEntityInstances || [];
      console.log('🔍 [DIRECT REPORTS FOUND]', {
        count: directReports.length,
        reports: directReports.map(dr => ({
          tei: dr.trackedEntityInstance,
          orgUnit: dr.orgUnit,
          positionTitle: extractAttributes(dr.attributes)[POSITION_ATTRIBUTES.POSITION_TITLE]
        }))
      });

      return directReports;
    } catch (error) {
      console.error('❌ Error fetching direct reports:', error);
      return [];
    }
  };


  useEffect(() => {
    const loadStaffList = async () => {
      if (!user?.organisationUnits?.[0]?.id) return;
      try {
        const orgUnitIds = user.organisationUnits?.map(ou => ou.id).join(';') || null;
        if (!orgUnitIds) return [];
        const { users } = await engine.query({
          users: {
            resource: 'users',
            params: {
              fields: 'id,username,name,firstName,surname,userCredentials[username],organisationUnits[id,name]',
              ou: orgUnitIds,
              ouMode: 'ACCESSIBLE',
              pageSize: 1000,
            },
          },
        });
        setStaffList(users.users || []);
      } catch (error) {
        console.error('Error loading staff list:', error);
        setStaffList([]);
      }
    };
    loadStaffList();
  }, [user?.organisationUnits?.[0]?.id, engine]);

  useEffect(() => {
    let isMounted = true;

    const loadReportingStructure = async () => {
      if (!user?.id) {
        console.log('⚠️ No user ID, skipping reporting structure load');
        if (isMounted) {
          setReportingStructureLoaded(true);
        }
        return;
      }

      console.log('🔍 [REPORTING DEBUG] Starting structure load for user:', user.id);

      try {
        // Get current position
        const posResult = await getCurrentUserPosition(user.id, user.organisationUnits);

        if (!posResult) {
          console.warn('⚠️ No position found for user:', user.id);
          if (isMounted) {
            setCurrentUserPosition(null);
            setReportingToPosition(null);
            setDirectReportPositions([]);
            setReportingStructureLoaded(true);
            setManagerAccessGranted(true);
          }
          return;
        }

        // ✅ Set current position FIRST
        if (isMounted) {
          setCurrentUserPosition(posResult);
        }

        // ✅ Grant access to user's own position org unit if needed
        if (posResult?.orgUnit && isMounted) {
          console.log('🔑 Checking access to user position org unit:', posResult.orgUnit);

          const userOrgUnits = user.organisationUnits?.map(ou => ou.id) || [];
          const userDataViewUnits = user.dataViewOrganisationUnits?.map(ou => ou.id) || [];

          const hasOrgUnitAccess = userOrgUnits.includes(posResult.orgUnit);
          const hasDataViewAccess = userDataViewUnits.includes(posResult.orgUnit);

          if (!hasOrgUnitAccess || !hasDataViewAccess) {
            console.log('🔑 User lacks access to their position org unit, granting access...');
            const accessGranted = await grantManagerOrgUnitAccess(user.id, posResult.orgUnit);
            if (accessGranted) {
              console.log('✅ User position org unit access granted successfully');
            } else {
              console.warn('⚠️ Failed to grant user position org unit access');
            }
          } else {
            console.log('✅ User already has access to their position org unit');
          }
        }

        // Get reporting to position and direct reports
        const [reportingTo, directReports] = await Promise.all([
          getReportingToPosition(posResult),
          getDirectReports(posResult)
        ]);

        console.log('📊 Reporting structure loaded:', {
          currentPosition: posResult.trackedEntityInstance,
          reportingTo: reportingTo?.trackedEntityInstance,
          directReports: directReports.length
        });

        // ✅ Set reporting structure data
        if (isMounted) {
          setReportingToPosition(reportingTo || null);
          setDirectReportPositions(directReports || []);
        }

        // ✅ CRITICAL FIX: Check and grant access to manager's org unit
        if (reportingTo?.orgUnit && isMounted) {
          console.log('🔑 Checking access to manager org unit:', reportingTo.orgUnit);

          const userOrgUnits = user.organisationUnits?.map(ou => ou.id) || [];
          const userDataViewUnits = user.dataViewOrganisationUnits?.map(ou => ou.id) || [];

          const hasOrgUnitAccess = userOrgUnits.includes(reportingTo.orgUnit);
          const hasDataViewAccess = userDataViewUnits.includes(reportingTo.orgUnit);

          if (!hasOrgUnitAccess || !hasDataViewAccess) {
            console.log('🔑 User lacks access to manager org unit, granting access...');
            const accessGranted = await grantManagerOrgUnitAccess(user.id, reportingTo.orgUnit);
            if (isMounted) {
              setManagerAccessGranted(!!accessGranted);
            }
            if (accessGranted) {
              console.log('✅ Manager org unit access granted successfully');
            } else {
              console.warn('⚠️ Failed to grant manager org unit access');
            }
          } else {
            console.log('✅ User already has access to manager org unit');
            if (isMounted) {
              setManagerAccessGranted(true);
            }
          }
        } else if (isMounted) {
          setManagerAccessGranted(true);
        }

        // ✅ NEW: Grant access to direct reports' org units
        if (directReports && directReports.length > 0 && isMounted) {
          console.log('🔑 Checking access to direct reports org units...');

          const userOrgUnits = user.organisationUnits?.map(ou => ou.id) || [];

          for (const directReport of directReports) {
            if (directReport?.orgUnit && !userOrgUnits.includes(directReport.orgUnit)) {
              console.log(`🔑 Granting access to direct report org unit: ${directReport.orgUnit}`);
              const accessGranted = await grantManagerOrgUnitAccess(user.id, directReport.orgUnit);
              if (accessGranted) {
                console.log(`✅ Direct report org unit access granted: ${directReport.orgUnit}`);
              } else {
                console.warn(`⚠️ Failed to grant access to direct report org unit: ${directReport.orgUnit}`);
              }
            }
          }
        }

        // ✅ CRITICAL FIX: Set loaded flag LAST, after all data is set
        // Use setTimeout to ensure state updates are processed
        if (isMounted) {
          // Small delay to ensure React batches all state updates
          setTimeout(() => {
            if (isMounted) {
              console.log('✅ [REPORTING STRUCTURE] Loaded successfully');
              setReportingStructureLoaded(true);
            }
          }, 100);
        }

      } catch (error) {
        console.error('❌ Error loading reporting structure:', error);
        // Still mark as loaded to prevent infinite loading
        if (isMounted) {
          setReportingStructureLoaded(true);
          setManagerAccessGranted(true);
        }
      }
    };

    loadReportingStructure();

    return () => {
      isMounted = false;
    };
  }, [user?.id, user?.organisationUnits, engine]);

  // In handleEditObjective function, fix lines 695-702:
  const handleEditObjective = async (objective) => {
      console.log('🟡 [PERFORMANCE] Edit objective clicked:', objective.id);

      // Get the enrollment ID from the objective
      let enrollmentId = objective.enrollment || objective.enrollmentId;
      
      if (!enrollmentId) {
          console.error('❌ No enrollment ID found for objective:', objective.id);
          // Try to fetch it from the objective details
          try {
              const { objective: objectiveDetails } = await fetchObjectiveWithDetails(objective.id, engine);
              enrollmentId = objectiveDetails.enrollment || objectiveDetails.enrollmentId || '';
          } catch (error) {
              console.warn('Could not fetch objective details for enrollment ID');
          }
      }

      // ✅ Load the editing objective
      await loadEditingObjective(objective.id);

      // Set the editing level based on the objective's role
      const levelId = getLevelFromRole(objective.objectiveRoleAssigned);
      setEditingLevelId(levelId);
  };

  // SEPARATE FUNCTION FOR CEO GOAL LINKAGE
  const handleCEOGoalLinkage = async (objective) => {
    console.log('🟢 [CEO GOAL LINKAGE] Handling CEO objective:', objective.id);

    const isCEO = user?.roleIds?.includes('nLSSLch316I');

    if (!isCEO) {
      console.log('🟡 [CEO GOAL LINKAGE] User is not CEO, skipping goal linkage');
      return;
    }

    try {
      const { objective: objectiveDetails } = await fetchObjectiveWithDetails(objective.id, engine);

      console.log('🟢 [CEO GOAL LINKAGE] CEO objective loaded:', {
        id: objectiveDetails.id,
        objectiveAlignment: objectiveDetails.objectiveAlignment,
        companyAlignedGoal: objectiveDetails.companyAlignedGoal,
        hasGoalLinkage: !!objectiveDetails.companyAlignedGoal,
        hasObjectiveAlignment: !!objectiveDetails.objectiveAlignment
      });

      // Set the editing objective
      setEditingObjective(objectiveDetails);
      setUiMode('edit');

      // ✅ CRITICAL FIX: Handle BOTH goal linkage AND objective alignment for CEO
      if (objectiveDetails.companyAlignedGoal) {
        console.log('🟢 [CEO GOAL LINKAGE] Found goal linkage:', objectiveDetails.companyAlignedGoal);

        // Set the selected goal for CEO
        setSelectedGoal(objectiveDetails.companyAlignedGoal);

        // Try to load goal details for display
        try {
          console.log('🟢 [CEO GOAL LINKAGE] Loading goal details:', objectiveDetails.companyAlignedGoal);
          const { objective: goalData } = await fetchObjectiveWithDetails(objectiveDetails.companyAlignedGoal, engine);

          if (goalData) {
            console.log('🟢 [CEO GOAL LINKAGE] Goal details loaded:', goalData.name);
            // The goal will be displayed in the Goal Selection section
          } else {
            console.warn('🟡 [CEO GOAL LINKAGE] Goal not found:', objectiveDetails.companyAlignedGoal);
          }
        } catch (goalError) {
          console.warn('🟡 [CEO GOAL LINKAGE] Error loading goal details:', goalError);
          // Non-critical error - goal selection will still work
        }
      } else {
        console.log('🟡 [CEO GOAL LINKAGE] No goal linkage found');
        setSelectedGoal('');
      }

      // ✅ CRITICAL FIX: CEO objectives CAN have objective alignment too!
      // Don't clear it - preserve objective alignment if it exists
      if (objectiveDetails.objectiveAlignment && objectiveDetails.objectiveRoleAssigned) {
        console.log('🟢 [CEO GOAL LINKAGE] CEO objective has objective alignment:', objectiveDetails.objectiveAlignment);

        // Set loading state for aligned objective
        setSelectedHigherLevelObjective({
          id: objectiveDetails.objectiveAlignment,
          name: 'Loading aligned objective...',
          description: 'Fetching objective details...',
          userRole: objectiveDetails.objectiveRoleAssigned,
          creatorRole: objectiveDetails.objectiveRoleAssigned,
          isLoading: true
        });

        // Load the aligned objective details
        try {
          console.log('🟢 [CEO GOAL LINKAGE] Loading aligned objective:', objectiveDetails.objectiveAlignment);
          const { objective: alignedObjective } = await fetchObjectiveWithDetails(objectiveDetails.objectiveAlignment, engine);

          if (alignedObjective) {
            console.log('🟢 [CEO GOAL LINKAGE] Aligned objective loaded:', alignedObjective.name);

            setSelectedHigherLevelObjective({
              id: alignedObjective.id,
              name: alignedObjective.name,
              description: alignedObjective.description || '',
              userRole: alignedObjective.objectiveRoleAssigned,
              creatorRole: alignedObjective.objectiveRoleAssigned,
              isLoading: false,
              isDeleted: false
            });
          } else {
            console.warn('🟡 [CEO GOAL LINKAGE] Aligned objective not found:', objectiveDetails.objectiveAlignment);
            setSelectedHigherLevelObjective({
              id: objectiveDetails.objectiveAlignment,
              name: 'Objective Not Found',
              description: 'This objective appears to have been deleted or is no longer accessible',
              userRole: objectiveDetails.objectiveRoleAssigned,
              creatorRole: objectiveDetails.objectiveRoleAssigned,
              isDeleted: true,
              isLoading: false
            });
          }
        } catch (alignmentError) {
          console.warn('🟡 [CEO GOAL LINKAGE] Error loading aligned objective:', alignmentError);
          setSelectedHigherLevelObjective({
            id: objectiveDetails.objectiveAlignment,
            name: 'Error Loading Objective',
            description: 'Could not load objective details: ' + alignmentError.message,
            userRole: objectiveDetails.objectiveRoleAssigned,
            creatorRole: objectiveDetails.objectiveRoleAssigned,
            isDeleted: true,
            isLoading: false,
            error: alignmentError.message
          });
        }
      } else {
        console.log('🟡 [CEO GOAL LINKAGE] No objective alignment found');
        setSelectedHigherLevelObjective(null);
      }

    } catch (error) {
      console.error('❌ [CEO GOAL LINKAGE] Failed to load CEO objective:', error);
      // Fallback
      setEditingObjective(objective);
      setUiMode('edit');
      setSelectedGoal('');
      setSelectedHigherLevelObjective(null);
    }
  };


  const loadHigherLevelObjectiveDetails = async (objectiveId) => {
    // ✅ CHECK CACHE FIRST
    if (objectiveErrorCache.has(objectiveId)) {
        console.log('🟡 [ALIGNMENT LOAD] Using cached error for objective:', objectiveId);
        const cachedError = objectiveErrorCache.get(objectiveId);
        createPlaceholderForDeletedObjective(objectiveId, cachedError);
        return null; // ✅ Return null instead of undefined
    }

    try {
        console.log('🟡 [ALIGNMENT LOAD] Fetching higher-level objective details:', objectiveId);

        const { objective: higherLevelObjective } = await fetchObjectiveWithDetails(objectiveId, engine);

        // ✅ ADD NULL CHECK AND PLACEHOLDER CREATION
        if (!higherLevelObjective || !higherLevelObjective.id) {
            console.warn('🟡 [ALIGNMENT LOAD] Higher-level objective not found:', objectiveId);
            objectiveErrorCache.set(objectiveId, 'Objective not found in response');
            createPlaceholderForDeletedObjective(objectiveId, 'Objective not found');
            return null; //  Return null
        }

        console.log('🟡 [ALIGNMENT LOAD] Found higher-level objective:', higherLevelObjective);

        // Update the selected objective with real details
        const objectiveWithDetails = {
            id: objectiveId,
            name: higherLevelObjective.name || `Objective ${objectiveId}`,
            description: higherLevelObjective.description || '',
            userRole: higherLevelObjective.objectiveRoleAssigned || '',
            creatorRole: higherLevelObjective.objectiveRoleAssigned || '',
            isDeleted: false,
            isLoading: false,
            dataValues: higherLevelObjective.rawDataValues || higherLevelObjective.dataValues || []
        };

        setSelectedHigherLevelObjective(objectiveWithDetails);
        console.log('🟡 [ALIGNMENT LOAD] Updated selectedHigherLevelObjective with details');
        
        return objectiveWithDetails; // ✅ Return the object

    } catch (error) {
        console.warn('🟡 [ALIGNMENT LOAD] Could not load higher-level objective details:', error);
        objectiveErrorCache.set(objectiveId, error.message);
        createPlaceholderForDeletedObjective(objectiveId, error.message);
        return null; // Return null
    }
};

  // ✅ EXTRACT PLACEHOLDER CREATION TO SEPARATE FUNCTION
  const createPlaceholderForDeletedObjective = (objectiveId, errorMessage) => {
    const placeholderObjective = {
      id: objectiveId,
      name: `Objective ${objectiveId} (Deleted)`,
      description: 'This objective appears to have been deleted or is no longer accessible',
      userRole: editingObjective?.objectiveRoleAssigned || '',
      creatorRole: editingObjective?.objectiveRoleAssigned || '',
      isDeleted: true,
      error: errorMessage,
      isLoading: false
    };

    console.log('🟡 [ALIGNMENT LOAD] Setting placeholder for deleted objective:', objectiveId);
    setSelectedHigherLevelObjective(placeholderObjective);
  };

  const handleAlignmentDetection = (objective) => {
    console.log('🟡 [ALIGNMENT DETECTION] Manual detection triggered for objective:', {
      id: objective.id,
      objectiveAlignment: objective.objectiveAlignment,
      objectiveRoleAssigned: objective.objectiveRoleAssigned
    });

    // ✅ Only proceed if we have valid alignment data AND we don't already have a valid selected objective
    if (objective.objectiveAlignment && objective.objectiveRoleAssigned &&
      (!selectedHigherLevelObjective || selectedHigherLevelObjective.isDeleted || selectedHigherLevelObjective.isLoading)) {

      console.log('🟡 [ALIGNMENT DETECTION] ✅ Found alignment data! Loading higher-level objective:', objective.objectiveAlignment);

      // Set loading state first
      setSelectedHigherLevelObjective({
        id: objective.objectiveAlignment,
        name: 'Loading aligned objective...',
        userRole: objective.objectiveRoleAssigned,
        creatorRole: objective.objectiveRoleAssigned,
        isLoading: true
      });

      // Then load the actual details
      loadHigherLevelObjectiveDetails(objective.objectiveAlignment);
    } else if (!objective.objectiveAlignment) {
      console.log('🟡 [ALIGNMENT DETECTION] ❌ No alignment data found');
      setSelectedHigherLevelObjective(null);
    } else {
      console.log('🟡 [ALIGNMENT DETECTION] ℹ️ Already have alignment data or no alignment needed');
    }
  };


const loadEditingObjective = async (eventId) => {
    console.log('🟡 [LOAD] Starting to load objective:', eventId);

    if (!eventId) {
        setEditingObjective(null);
        setSelectedHigherLevelObjective(null);
        setSelectedGoal('');
        return;
    }

    setLoading(true);

    try {
        const { objective: objectiveDetails, kras, kpis } = await fetchObjectiveWithDetails(eventId, engine);

        console.log('🟡 [LOAD] fetchObjectiveWithDetails completed, user role:', user?.roleIds?.[0]);
        console.log('🔍 [LOAD] KRAs received:', kras);
        console.log('🔍 [LOAD] KPIs received:', kpis);

        const mappedObjective = {
            id: objectiveDetails.id || '',
            name: objectiveDetails.name || '',
            description: objectiveDetails.description || '',
            weight: objectiveDetails.weight || 0,
            status: objectiveDetails.status || '',
            startDate: objectiveDetails.startDate ? objectiveDetails.startDate.slice(0, 10) : new Date().toISOString().slice(0, 10),
            completionDate: objectiveDetails.completionDate || '',
            orgUnit: objectiveDetails.orgUnit || user?.organisationUnits?.[0]?.id || '',
            remarks: objectiveDetails.remarks || '',
            score: objectiveDetails.score || 0,
            performanceYear: objectiveDetails.performanceYear || new Date().getFullYear(),
            workType: objectiveDetails.workType || '',
            companyAlignedGoal: objectiveDetails.companyAlignedGoal || '',
            objectiveAlignment: objectiveDetails.objectiveAlignment,
            objectiveRoleAssigned: objectiveDetails.objectiveRoleAssigned,
            higherLevelObjectiveId: objectiveDetails.higherLevelObjectiveId,
            enrollmentId: objectiveDetails.enrollment || objectiveDetails.enrollmentId || '',
            trackedEntityInstance: objectiveDetails.trackedEntityInstance || '',
            // CRITICAL: Include the actual KRA and KPI data
            kras: kras.length > 0 ? kras : [{ name: '', description: '', weight: 0 }],
            kpis: kpis.length > 0 ? kpis : [{ name: '', description: '', target: '' }],
        };

        console.log('🟡 [LOAD] About to setEditingObjective with data:', {
            objectiveAlignment: mappedObjective.objectiveAlignment,
            companyAlignedGoal: mappedObjective.companyAlignedGoal,
            userRole: mappedObjective.objectiveRoleAssigned,
            isCEO: user?.roleIds?.includes('nLSSLch316I'),
            enrollmentId: mappedObjective.enrollmentId,
            krasCount: mappedObjective.kras.length,
            kpisCount: mappedObjective.kpis.length
        });

        // ✅ CRITICAL: Set the editing objective FIRST with all data
        setEditingObjective(mappedObjective);
        setUiMode('edit');

        // Then handle alignment separately
        const isCEO = user?.roleIds?.includes('nLSSLch316I');

        if (isCEO) {
            console.log('🟢 [LOAD] User is CEO - handling goal linkage');
            await handleCEOGoalLinkage(mappedObjective);
        } else {
            console.log('🟢 [LOAD] User is not CEO - handling objective alignment');
            await handleRegularObjectiveAlignment(mappedObjective);
        }

    } catch (error) {
        console.error('[loadEditingObjective] Failed:', error);
        setEditingObjective(null);
        setSelectedHigherLevelObjective(null);
        setSelectedGoal('');
    } finally {
        setLoading(false);
    }
};

  // SEPARATE FUNCTION FOR REGULAR OBJECTIVE ALIGNMENT
 const handleRegularObjectiveAlignment = async (mappedObjective) => {
    console.log('🟢 [REGULAR ALIGNMENT] Handling non-CEO objective:', mappedObjective.id);

    // Clear goal selection for non-CEO users
    setSelectedGoal('');

    // Handle objective-to-objective alignment
    if (mappedObjective.objectiveAlignment && mappedObjective.objectiveRoleAssigned) {
        console.log('🟡 [REGULAR ALIGNMENT] Found alignment data, loading higher-level objective:', mappedObjective.objectiveAlignment);

        // Set loading state first
        setSelectedHigherLevelObjective({
            id: mappedObjective.objectiveAlignment,
            name: 'Loading aligned objective...',
            description: 'Fetching objective details...',
            userRole: mappedObjective.objectiveRoleAssigned,
            creatorRole: mappedObjective.objectiveRoleAssigned,
            isLoading: true
        });

        // Try to load the actual aligned objective
        try {
            console.log('🟡 [REGULAR ALIGNMENT] Attempting to load aligned objective:', mappedObjective.objectiveAlignment);
            const { objective: alignedObjective } = await fetchObjectiveWithDetails(mappedObjective.objectiveAlignment, engine);

            if (alignedObjective) {
                console.log('🟡 [REGULAR ALIGNMENT] Successfully loaded aligned objective:', alignedObjective.name);

                setSelectedHigherLevelObjective({
                    id: alignedObjective.id,
                    name: alignedObjective.name,
                    description: alignedObjective.description || '',
                    userRole: alignedObjective.objectiveRoleAssigned,
                    creatorRole: alignedObjective.objectiveRoleAssigned,
                    isLoading: false,
                    isDeleted: false
                });
            } else {
                console.warn('🟡 [REGULAR ALIGNMENT] Aligned objective not found:', mappedObjective.objectiveAlignment);
                setSelectedHigherLevelObjective({
                    id: mappedObjective.objectiveAlignment,
                    name: 'Objective Not Found',
                    description: 'This objective appears to have been deleted or is no longer accessible',
                    userRole: mappedObjective.objectiveRoleAssigned,
                    creatorRole: mappedObjective.objectiveRoleAssigned,
                    isDeleted: true,
                    isLoading: false
                });
            }
        } catch (alignmentError) {
            console.warn('🟡 [REGULAR ALIGNMENT] Error loading aligned objective:', alignmentError);
            setSelectedHigherLevelObjective({
                id: mappedObjective.objectiveAlignment,
                name: 'Error Loading Objective',
                description: 'Could not load objective details: ' + alignmentError.message,
                userRole: mappedObjective.objectiveRoleAssigned,
                creatorRole: mappedObjective.objectiveRoleAssigned,
                isDeleted: true,
                isLoading: false,
                error: alignmentError.message
            });
        }
    } else {
        console.log('🟡 [REGULAR ALIGNMENT] No alignment data found, clearing selectedHigherLevelObjective');
        setSelectedHigherLevelObjective(null);
    }
};


  const contextValue = useMemo(() => {
    return {
      user,
      role: user?.role,
      data,
      events,
      currentUserPosition,
      reportingToPosition,
      directReportPositions,
      reportingStructureLoaded,
      managerAccessGranted,
      managerAccessGranted,
      staffList,
      loading,
      loadingEvents,
      kraEnabled: isKraEnabled,
      setData,
      setEvents,
      onSaveCallback,
      setOnSaveCallback,
      onErrorCallback,
      setOnErrorCallback,
      setLoading,
      setLoadingEvents,
      uiMode,
      setUiMode,
      editingLevelId,
      setEditingLevelId,
      editingObjective,
      selectedGoal, setSelectedGoal,
      handleEditObjective,
      getLevelFromRole,
      setEditingObjective,
      expandedLevels,
      setExpandedLevels,
      expandedObjectives,
      selectedHigherLevelObjective,
      setSelectedHigherLevelObjective,
      selectedGoal,
      setSelectedGoal,
      loadHigherLevelObjectiveDetails,
      setExpandedObjectives,
      canView,
      canUserEditLevel,
      canUserDeleteLevel,
      toggleLevelExpansion,
      toggleObjectiveExpansion,
      handleCEOGoalLinkage,
      handleRegularObjectiveAlignment,
      loadEditingObjective,
      getLevelFromRole,
      handleAlignmentDetection,
      createPlaceholderForDeletedObjective,
      protectedObjectives,
      childCounts,
      setProtectedObjectives,
      setChildCounts,

      onEdit: async (levelId, objective) => {
        setEditingLevelId(levelId);
        const eventId = objective.id || objective.event;
        if (eventId && loadEditingObjective) {
          await loadEditingObjective(eventId);
        } else {
          setEditingObjective(objective);
          setUiMode('edit');
        }
      },
      onDelete: async (levelId, objectiveId) => {
        if (!canUserDeleteLevel(levelId)) return;
        // Delete implementation
      }
    };
  }, [
    user, data, events, loading, loadingEvents, uiMode, editingLevelId,
    editingObjective, expandedLevels, expandedObjectives,
    selectedHigherLevelObjective, selectedGoal,
    selectedHigherLevelObjective, onSaveCallback, setOnSaveCallback,
    onErrorCallback, setOnErrorCallback, canView, canUserEditLevel,
    canUserDeleteLevel, toggleLevelExpansion, toggleObjectiveExpansion,
    handleCEOGoalLinkage, handleRegularObjectiveAlignment,
    handleEditObjective, loadEditingObjective, getLevelFromRole,
    handleAlignmentDetection, createPlaceholderForDeletedObjective,
    protectedObjectives, childCounts, setProtectedObjectives, setChildCounts, isKraEnabled
  ]);


  return (
    <PerformanceContext.Provider value={contextValue}>
      <PerformanceManagementCore />
    </PerformanceContext.Provider>
  );
}

function PerformanceManagementCore() {
  const { canDeleteLevel, canEditLevel } = usePermissions();
  const {
    user,
    data,
    events,
    setData,
    setEvents,
    loading,
    loadingEvents,
    setLoading,
    setLoadingEvents,
    managerAccessGranted,
    currentUserPosition,
    grantManagerOrgUnitAccess,
    uiMode,
    setUiMode,
    editingLevelId,
    setEditingLevelId,
    editingObjective,
    setEditingObjective,
    selectedHigherLevelObjective,
    setSelectedHigherLevelObjective,
    loadEditingObjective,
    reportingToPosition,
    directReportPositions,
    reportingStructureLoaded,
    kraEnabled,

  } = useContext(PerformanceContext);

  const [error, setError] = useState(null);
  const [accessibleOrgUnits, setAccessibleOrgUnits] = useState([]);
  const [orgUnits, setOrgUnits] = useState([]);
  const engine = useDataEngine();
  const isMountedRef = useRef(true);

    // Helper: Find the top-level org unit from user's org units
  const findStrategicOrgUnit = async (engine, programId, preferredLevels = [4, 5, 6]) => {
  try {
    // Get program details
    const programResponse = await engine.query({
      programDetails: {
        resource: `programs/${programId}`,
        params: { 
          fields: 'id,name,organisationUnits[id,name,level,path]'
        }
      }
    });

    const program = programResponse.programDetails;
    const assignedOrgUnits = program.organisationUnits || [];

    if (assignedOrgUnits.length === 0) {
      console.warn(`⚠️ Program ${programId} not assigned to any org unit`);
      return null;
    }

    // Try to find org unit at preferred strategic levels
    for (const level of preferredLevels) {
      const strategicOU = assignedOrgUnits.find(ou => ou.level === level);
      if (strategicOU) {
        console.log(`🎯 Found strategic org unit at Level ${level}:`, strategicOU.name);
        return strategicOU;
      }
    }

    // If no strategic level found, return the highest level org unit
    const highestLevelOU = assignedOrgUnits.reduce((highest, current) => 
      (current.level > highest.level) ? current : highest
    );
    
    console.log(`🎯 Using highest level org unit (Level ${highestLevelOU.level}):`, highestLevelOU.name);
    return highestLevelOU;

  } catch (error) {
    console.error('❌ Error finding strategic org unit:', error);
    return null;
  }
};

  // Inside PerformanceManagementWrapper()
  const saveFinancialCalendarToStorage = (config) => {
    try {
      localStorage.setItem('company_financial_calendar', JSON.stringify(config));
      // 🔥 Broadcast to all listening components (like ObjectiveForm)
      window.dispatchEvent(new Event('financialCalendarUpdated'));
      console.log('✅ Financial calendar saved and broadcasted:', config);
    } catch (error) {
      console.error('❌ Failed to save financial calendar to localStorage:', error);
    }
  };

  
  
  useEffect(() => {
    const loadFinancialYearConfig = async () => {
      
      // Example: fetching from a Program Rule Variable, Constant, or custom endpoint
      try {
        
        const response = await engine.query({
          config: {
            resource: 'constants',
            params: {
              filter: 'name:eq:FINANCIAL_YEAR_CONFIG',
              fields: 'value'
            }
          }
        });

        const configJson = response.config.constants?.[0]?.value;
        if (configJson) {
          const config = JSON.parse(configJson);
          saveFinancialCalendarToStorage(config);
          return;
        }
      } catch (e) {
        console.warn('Could not load financial config from database constants');
      }

      // Fallback: use default if needed*************TO BE REVIEWED IN FUTURE POSSIBILITY TO DROP IT 
      const fallbackConfig = {
        companyId: user?.organisationUnits?.[0]?.id || 'default',
        companyName: 'Default Org',
        financialYearStart: '2025-07-01',
        financialYearEnd: '2026-06-30',
        lastUpdated: new Date().toISOString(),
        isActive: true
      };
      saveFinancialCalendarToStorage(fallbackConfig);
    };

    if (user && engine) {
      loadFinancialYearConfig();
    }
  }, [user, engine]);

  // Add this debug to verify context updates
  useEffect(() => {
    console.log('🟡 [CONTEXT DEBUG] PerformanceContext updated:', {
      editingObjective: editingObjective?.id,
      selectedHigherLevelObjective: selectedHigherLevelObjective?.id,
      selectedHigherLevelObjectiveName: selectedHigherLevelObjective?.name
    });
  }, [editingObjective, selectedHigherLevelObjective]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const safeSetState = (setter, value) => {
    if (isMountedRef.current) {
      setter(value);
    }
  };

  const handleDeleteObjective = async (levelId, objectiveId) => {
    console.log('Delete called for:', levelId, objectiveId);
    if (!canDeleteLevel(levelId)) {
      alert('Not authorized');
      return;
    }

    try {
      const authHeader = getAuthHeader();
      await useDataQuery.delete(`${BASE_URL}/api/events/${objectiveId}`, {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
      });

      safeSetState(setData, oldData =>
        oldData.map(level =>
          level.level === levelId
            ? {
              ...level,
              objectives: level.objectives.filter(obj => obj.id !== objectiveId)
            }
            : level
        )
      );
      alert('Objective deleted successfully!');
    } catch (error) {
      console.error('Delete failed:', error);
      alert(`Failed to delete objective: ${error.message}`);
    }
  };



  useEffect(() => {
    let active = true;

    const loadAccessibleOrgUnits = async () => {
      if (!user?.organisationUnits) return;

      try {
        const verified = await Promise.all(
          user.organisationUnits.map(async ou => {
            try {
              const response = await engine.query({
                ouPrograms: {
                  resource: `organisationUnits/${ou.id}`,
                  params: { fields: 'programs[id]' }
                }
              });

              const hasAccess = response.ouPrograms.programs?.some(
                p => p.id === programId
              ) || false;

              return { ...ou, accessible: hasAccess };
            } catch (err) {
              return { ...ou, accessible: false };
            }
          })
        );

        if (active) safeSetState(setAccessibleOrgUnits, verified.filter(ou => ou.accessible));
      } catch (err) {
        if (active) {
          console.error('Failed to load accessible org units:', err);
          safeSetState(setError, err.message);
        }
      }
    };

    loadAccessibleOrgUnits();
    return () => { active = false; };
  }, [user, engine]);

  useEffect(() => {
    let active = true;

    const loadProgramOrgUnits = async () => {
      try {
        const { organisationUnits } = await engine.query({
          organisationUnits: {
            resource: `programs/${programId}/organisationUnits`,
            params: { fields: 'id,name' }
          }
        });
        if (active) safeSetState(setOrgUnits, organisationUnits || []);
      } catch (error) {
        if (active) console.error('Failed to load program org units:', error);
      }
    };

    loadProgramOrgUnits();
    return () => { active = false; };
  }, [engine]);


  const verifyObjectiveLinkage = (events, positionTEI) => {
    console.log('🔍 [VERIFY LINKAGE] Checking objectives for TEI:', positionTEI);

    events?.forEach(event => {
      const positionRef = event.dataValues?.find(dv =>
        dv.dataElement === 'SlgO60cMK3F'
      );

      console.log('Event:', event.event, 'has positionRef:', positionRef?.value);

      if (positionRef?.value === positionTEI) {
        console.log('✅ This objective belongs to user:', event.event);
      }
    });
  };


useEffect(() => {
  let active = true;
  
  const fetchAllRequiredData = async () => {
    console.log('🔍 [FETCH ALL DATA] Starting with:', {
      user: user?.username,
      userOrgUnit: user?.organisationUnits?.[0],
    });

    if (!user?.organisationUnits?.length || !currentUserPosition || !reportingStructureLoaded) {
      console.log('⚠️ [FETCH ALL DATA] Missing prerequisites, skipping');
      return;
    }

    try {
      safeSetState(setLoading, true);
      safeSetState(setLoadingEvents, true);

      const strategicOrgUnit = await findStrategicOrgUnit(engine, programId, [4, 5, 6, 7, 8, 9, 10]);
    
    if (!strategicOrgUnit) {
      console.error('❌ Could not find any org unit with program assigned');
      if (active) {
        safeSetState(setError, 
          'Performance Management program is not assigned to any department.\n\n' +
          'Please contact your administrator to assign the program to appropriate org units.'
        );
      }
      return;
    }

      // ✅ DYNAMIC: Get user's org unit
      const userOrgUnit = user.organisationUnits[0];
      console.log('✅ [FETCH ALL DATA] User org unit:', {
        id: userOrgUnit.id,
        name: userOrgUnit.name,
        level: userOrgUnit.level,
        path: userOrgUnit.path
      });

      // ✅ DYNAMIC: Get the top-level org unit from user's path
      let topLevelOrgUnitId = userOrgUnit.id;
      if (userOrgUnit.path) {
        const pathParts = userOrgUnit.path.split('/').filter(id => id);
        if (pathParts.length > 0) {
          topLevelOrgUnitId = pathParts[0]; // First in path is the root/top-level
          console.log('🌳 [TOP LEVEL ORG UNIT] Found:', topLevelOrgUnitId);
        }
      }

      // ✅ DYNAMIC: Check program assignment for user's org unit
      console.log('🔍 [PROGRAM CHECK] Checking assignment for org unit:', userOrgUnit.id);
      
      try {
        // First, get program details to see where it's assigned
        const programResponse = await engine.query({
          programDetails: {
            resource: `programs/${programId}`,
            params: { 
              fields: 'id,name,organisationUnits[id,name,level,path]'
            }
          }
        });

        const program = programResponse.programDetails;
        const assignedOrgUnits = program.organisationUnits || [];
        
        // Check if program is assigned to user's org unit or any ancestor
        const isDirectlyAssigned = assignedOrgUnits.some(ou => ou.id === userOrgUnit.id);
        
        // Check inheritance through path
        let isInherited = false;
        let assignedAncestor = null;
        
        if (userOrgUnit.path && !isDirectlyAssigned) {
          const userPathParts = userOrgUnit.path.split('/').filter(id => id);
          for (const pathId of userPathParts) {
            if (assignedOrgUnits.some(ou => ou.id === pathId)) {
              isInherited = true;
              assignedAncestor = pathId;
              break;
            }
          }
        }

        const hasProgramAccess = isDirectlyAssigned || isInherited;
        
        console.log('🔍 [PROGRAM ACCESS]', {
          userOrgUnit: userOrgUnit.id,
          programName: program.name,
          directlyAssigned: isDirectlyAssigned,
          inherited: isInherited,
          assignedAncestor,
          assignedOrgUnits: assignedOrgUnits.map(ou => ({id: ou.id, level: ou.level})),
          hasAccess: hasProgramAccess
        });

        if (!hasProgramAccess) {
          console.error('❌ [NO PROGRAM ACCESS] User cannot access program from org unit:', userOrgUnit.id);
          if (active) {
            safeSetState(setError, 
              `Performance Management program is not assigned to your department or any parent department.\n\n` +
              `Please contact your administrator to assign the program to:\n` +
              `• Your department: ${userOrgUnit.id} (Level ${userOrgUnit.level})\n` +
              `• Or a parent department in your hierarchy`
            );
          }
          return;
        }

      } catch (programError) {
        console.error('❌ [PROGRAM CHECK FAILED]', programError);
        if (active) {
          safeSetState(setError, 'Failed to check program assignment. Please contact administrator.');
        }
        return;
      }

      // ✅ DYNAMIC: Build query based on user's position and reporting chain
      console.log('🌐 [QUERY BUILDING] Creating query for reporting chain...');
      
      // Collect all position TEIs in reporting chain
      const positionTeisInChain = new Set();
      
      // Add current user's position
      if (currentUserPosition?.trackedEntityInstance) {
        positionTeisInChain.add(currentUserPosition.trackedEntityInstance);
      }
      
      // Add manager's position (if exists)
      if (reportingToPosition?.trackedEntityInstance) {
        positionTeisInChain.add(reportingToPosition.trackedEntityInstance);
      }
      
      // Add direct reports' positions
      directReportPositions.forEach(pos => {
        if (pos?.trackedEntityInstance) {
          positionTeisInChain.add(pos.trackedEntityInstance);
        }
      });
      
      console.log('🔗 [REPORTING CHAIN] Position TEIs:', Array.from(positionTeisInChain));

      if (positionTeisInChain.size === 0) {
        console.warn('⚠️ No position TEIs found in reporting chain');
        if (active) {
          safeSetState(setEvents, []);
          safeSetState(setData, []);
        }
        return;
      }

      // ✅ DYNAMIC: Create filter for all position TEIs in chain
      const positionFilter = Array.from(positionTeisInChain)
        .map(tei => `SlgO60cMK3F:eq:${tei}`)
        .join(';');
      
      console.log('🎯 [POSITION FILTER]', positionFilter);

      // ✅ DYNAMIC: Query using user's org unit with ACCESSIBLE mode
      // This will include all org units the user has access to
      const response = await engine.query({
        allObjectives: {
          resource: 'events',
          params: {
            program: programId,
            programStage: programStageId,
            orgUnit: userOrgUnit.id,  // Start from user's org unit
            ouMode: 'ACCESSIBLE',      // Includes all accessible org units
            filter: positionFilter,    // Filter by position TEIs in chain
            fields: 'event,orgUnit,trackedEntityInstance,enrollment,eventDate,dataValues[dataElement,value]',
            pageSize: 1000,
          },
        },
      });

      const allEvents = response.allObjectives?.events || [];
      console.log('✅ [QUERY RESULTS] Objectives found:', allEvents.length);

      // Log breakdown by position
      const byPosition = {};
      allEvents.forEach(event => {
        const positionTei = event.dataValues?.find(dv => dv.dataElement === 'SlgO60cMK3F')?.value;
        if (positionTei) {
          byPosition[positionTei] = (byPosition[positionTei] || 0) + 1;
        }
      });
      
      console.log('📊 [BREAKDOWN BY POSITION]', byPosition);

      if (active) {
        safeSetState(setEvents, allEvents);
        safeSetState(setData, allEvents);
        safeSetState(setError, null); // Clear any previous errors
      }

    } catch (error) {
      console.error('❌ [FETCH ALL DATA FAILED]', error);
      if (active) {
        safeSetState(setError, 
          `Failed to load objectives: ${error.message}\n\n` +
          `Please ensure:\n` +
          `1. You have access to the Performance Management program\n` +
          `2. Your position is properly configured in the system\n` +
          `3. Contact administrator if issue persists`
        );
        safeSetState(setEvents, []);
        safeSetState(setData, []);
      }
    } finally {
      if (active) {
        safeSetState(setLoading, false);
        safeSetState(setLoadingEvents, false);
      }
    }
  };

  fetchAllRequiredData();
  return () => { active = false; };
}, [
  user,
  engine,
  reportingStructureLoaded,
  currentUserPosition,
  reportingToPosition,
  directReportPositions,
  // Use JSON.stringify for deep comparison of arrays
  JSON.stringify(directReportPositions.map(dr => dr.trackedEntityInstance))
]);


  if (loading) return <div>Loading...</div>;

  return (
    <div style={{ maxWidth: 1200, margin: 'auto', padding: 16 }}>
      <h3 style={{ color: '#1c9420ff' }}>Objectives Management</h3>

      {loadingEvents ? (
        <div>Loading objectives...</div>
      ) : uiMode === 'add' || uiMode === 'edit' ? (
          <div>
          <button
            style={{display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 16px',
                    backgroundColor: colors.blue800,
                    color: 'white',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 500,
                    boxShadow: '0 4px 12px rgba(18, 36, 200, 0.5)',
                    border: 1,
                    marginBottom: 15,
                  }}
            primary onClick={() => {
              setUiMode('list');
              setEditingLevelId(null);
              setEditingObjective(null);
              setSelectedHigherLevelObjective(null);
          }}> <IconChevronLeft16 /> Back to Objectives List
          </button>

          <ObjectiveForm
              mode={uiMode}
              onComplete={() => {
                  console.log('✅ [PARENT] onComplete callback triggered');
                                }}
              objective={editingObjective}
              selectedHigherLevelObjective={selectedHigherLevelObjective}
              setSelectedHigherLevelObjective={setSelectedHigherLevelObjective}
              onSave={() => {
                  setUiMode('list');
                  setEditingLevelId(null);
                  setEditingObjective(null);
                  setSelectedHigherLevelObjective(null);
              }}
              onCancel={() => {
                  setUiMode('list');
                  setSelectedHigherLevelObjective(null);
              }}
              kraEnabled={kraEnabled}
          />
          </div>
          ) : (
        <>
          <PerformanceList
               onEdit={async (levelId, objective) => {
                 setEditingLevelId(levelId);
                 const eventId = objective.id || objective.event;
                 if (eventId) {
                   await loadEditingObjective(eventId);
                   setUiMode('edit');
                 } else {
                   setEditingObjective(objective);
                   setUiMode('edit');
                 }
               }}
               onDelete={handleDeleteObjective}
               onAddObjective={() => {
                 // Clear all editing state when creating new objective
                 setEditingLevelId(null);
                 setEditingObjective(null);
                 setSelectedHigherLevelObjective(null);
                 setUiMode('add');
               }}
               kraEnabled={kraEnabled}
           />
           </>
       )}
    </div>



  );
}

export const usePerformanceContext = () => {
  const context = useContext(PerformanceContext);
  if (!context) {
    throw new Error('usePerformanceContext must be used within a PerformanceManagementWrapper');
  }
  return context;
};